import { AI_REVIEW_TABS } from '@/lib/constants';

export type AiReviewSettingsTab = (typeof AI_REVIEW_TABS)[keyof typeof AI_REVIEW_TABS];

export const AI_REVIEW_SETTINGS_CARD_IDS = {
  CODE_REVIEW_AGENT: 'ai-review-card-code-review-agent',
  SENSITIVITY: 'ai-review-card-sensitivity',
  INCREMENTAL_REVIEWS: 'ai-review-card-incremental-reviews',
  IGNORE_PATTERNS: 'ai-review-card-ignore-patterns',
  IGNORE_PULL_REQUEST_PATTERNS: 'ai-review-card-ignore-pull-request-patterns',
  MAX_CHANGED_LINES: 'ai-review-card-max-changed-lines',
  RESOLVE_THREADS: 'ai-review-card-resolve-threads',
  CUSTOM_INSTRUCTIONS: 'ai-review-card-custom-instructions',
  DOCUMENTATION_LINKS: 'ai-review-card-documentation-links',
  ARCHITECTURE_DIAGRAMS: 'ai-review-card-architecture-diagrams',
  EXTERNAL_CONTRIBUTOR_MANUAL_REVIEW: 'ai-review-card-external-contributor-manual-review',
  LINKED_REPOSITORIES: 'ai-review-card-linked-repositories',
  MERGE_CONFIDENCE: 'ai-review-card-merge-confidence',
  AUTO_APPROVE: 'ai-review-card-auto-approve',
  ULTRAREVIEW_MODE: 'ai-review-card-ultrareview-mode',
  AUTO_ULTRAREVIEW: 'ai-review-card-auto-ultrareview',
  DRAFT_REVIEWS: 'ai-review-card-draft-reviews',
  PR_DESCRIPTIONS: 'ai-review-card-pr-descriptions',
  DESCRIPTION_INSTRUCTIONS: 'ai-review-card-description-instructions',
  PR_DESCRIPTION_REVIEW_LINK: 'ai-review-card-pr-description-review-link',
  PR_DESCRIPTION_SKIP_IF_AUTHOR_PROVIDED: 'ai-review-card-pr-description-skip-if-author-provided',
  LEARNINGS: 'ai-review-card-learnings',
  AUTO_FIX_LOOP: 'ai-review-card-auto-fix-loop',
  FIX_WITH_CUBIC_TAGS: 'ai-review-card-fix-with-cubic-tags',
  FIX_WITH_CUBIC_BUTTON: 'ai-review-card-fix-with-cubic-button',
  AUTO_FIX_PROVIDER: 'ai-review-card-auto-fix-provider',
  CODING_AGENT_INSTRUCTIONS: 'ai-review-card-coding-agent-instructions',
} as const;

export type AiReviewSettingsCardId =
  (typeof AI_REVIEW_SETTINGS_CARD_IDS)[keyof typeof AI_REVIEW_SETTINGS_CARD_IDS];

export type AiReviewSettingsCard = {
  id: AiReviewSettingsCardId;
  label: string;
  tab: AiReviewSettingsTab;
  keywords: string[];
  isExperimental?: boolean;
  commandEnabled?: boolean;
};

export const AI_REVIEW_SETTINGS_CARDS: AiReviewSettingsCard[] = [
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.CODE_REVIEW_AGENT,
    label: 'AI review - Code review agent',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'general review',
      'code review agent',
      'review agent',
      'enable review',
      'toggle',
      'enable',
      'disable',
      'turn on',
      'automatic',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.SENSITIVITY,
    label: 'AI review - Sensitivity',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'sensitivity',
      'thoroughness',
      'detail level',
      'review depth',
      'strictness',
      'noise',
      'noisy',
      'strict',
      'lenient',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.INCREMENTAL_REVIEWS,
    label: 'AI review - Incremental reviews',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'incremental reviews',
      'incremental commits',
      'review new commits',
      'new commits',
      'push',
      're-review',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.IGNORE_PATTERNS,
    label: 'AI review - Ignore files',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'ignore',
      'ignore files',
      'ignore patterns',
      'ignore file patterns',
      'ignored files',
      'ignored patterns',
      'skip review',
      'exclude',
      'globs',
      'filter',
      'skip files',
      'exclude files',
      'file path',
      'vendor',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.IGNORE_PULL_REQUEST_PATTERNS,
    label: 'AI review - Ignore pull requests',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'ignore',
      'ignore pull requests',
      'ignore prs',
      'ignored pull requests',
      'ignored prs',
      'skip pull requests',
      'skip prs',
      'ignore pr',
      'exclude pull requests',
      'exclude prs',
      'pull request patterns',
      'pr label',
      'wip',
      'skip-review',
      'branches',
      'labels',
      'title pattern',
      'draft',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.MAX_CHANGED_LINES,
    label: 'AI review - Maximum changed lines',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'max changed lines',
      'maximum changed lines',
      'changed lines',
      'diff size',
      'large pull request',
      'large pr',
      'oversized pull request',
      'oversized pr',
      'budget',
      'skip large prs',
      'skip oversized prs',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.RESOLVE_THREADS,
    label: 'AI review - Auto-resolve threads',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'resolve threads',
      'auto resolve',
      'resolve when addressed',
      'close',
      'dismiss',
      'auto close',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.CUSTOM_INSTRUCTIONS,
    label: 'AI review - Custom instructions',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'custom instructions',
      'review instructions',
      'prompt',
      'system prompt',
      'guidelines',
      'customize',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.DOCUMENTATION_LINKS,
    label: 'AI review - Documentation links',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'documentation',
      'docs',
      'library docs',
      'links',
      'packages',
      'libraries',
      'dependencies',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.ARCHITECTURE_DIAGRAMS,
    label: 'AI review - Architecture diagrams',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'architecture diagrams',
      'mermaid',
      'diagram',
      'visualization',
      'flowchart',
      'visual',
    ],
    isExperimental: true,
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.EXTERNAL_CONTRIBUTOR_MANUAL_REVIEW,
    label: 'AI review - External contributor manual review',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'external contributors',
      'manual review',
      'manual review trigger',
      'public repos',
      'outside contributors',
      'review gate',
      'trusted manual review',
    ],
    isExperimental: true,
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.LINKED_REPOSITORIES,
    label: 'AI review - Cross-repo reviews',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'cross-repo reviews',
      'cross repository reviews',
      'linked repositories',
      'linked repos',
      'multi-repo',
      'cross-repo',
      'companion',
      'companion pr',
      'sibling repo',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.MERGE_CONFIDENCE,
    label: 'AI review - Merge confidence summary',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'merge confidence',
      'confidence score',
      'risk score',
      'merge risk',
      'safe to merge',
      'risk assessment',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.AUTO_APPROVE,
    label: 'AI review - Auto-approve PRs',
    tab: AI_REVIEW_TABS.AUTO_APPROVE,
    keywords: [
      'auto approve',
      'automatic approval',
      'approve prs',
      'approve when no issues',
      'low risk',
      'auto-approve',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.ULTRAREVIEW_MODE,
    label: 'AI review - Ultrareview mode',
    tab: AI_REVIEW_TABS.ULTRAREVIEW,
    keywords: [
      'ultrareview',
      'ultrareview mode',
      'disable ultrareview',
      'enable ultrareview',
      'manual',
      'automatic',
      'deep review',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.AUTO_ULTRAREVIEW,
    label: 'AI review - Automatic ultrareviews',
    tab: AI_REVIEW_TABS.ULTRAREVIEW,
    keywords: [
      'ultrareview',
      'ultra review',
      'ultra',
      'auto ultrareview',
      'deep review',
      'auto-trigger',
      'automatic ultrareviews',
      'risk-based trigger',
      'risk policy',
      'high risk',
      'risky',
      'file-based trigger',
      'file rules',
      'migrations',
      'thorough review',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.DRAFT_REVIEWS,
    label: 'AI review - Draft PR reviews',
    tab: AI_REVIEW_TABS.SETUP,
    keywords: [
      'draft',
      'draft pr',
      'draft pull request',
      'review drafts',
      'wip',
      'work in progress',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.PR_DESCRIPTIONS,
    label: 'AI review - AI PR descriptions',
    tab: AI_REVIEW_TABS.PR_DESCRIPTIONS,
    keywords: [
      'pr descriptions',
      'pull request description',
      'summary',
      'generate description',
      'auto description',
      'pr body',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.DESCRIPTION_INSTRUCTIONS,
    label: 'AI review - Description instructions',
    tab: AI_REVIEW_TABS.PR_DESCRIPTIONS,
    keywords: ['description instructions', 'summary instructions', 'pr description prompt'],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.PR_DESCRIPTION_REVIEW_LINK,
    label: 'AI review - PR description review link',
    tab: AI_REVIEW_TABS.PR_DESCRIPTIONS,
    keywords: [
      'view guided diff',
      'review link',
      'cubic link',
      'pr description footer',
      'review in cubic',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.PR_DESCRIPTION_SKIP_IF_AUTHOR_PROVIDED,
    label: 'AI review - Skip when authors write descriptions',
    keywords: [
      'skip description',
      'author description',
      'existing description',
      'manual description',
      'skip generation',
      'respect author',
    ],
    tab: AI_REVIEW_TABS.PR_DESCRIPTIONS,
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.LEARNINGS,
    label: 'AI review - Learning feedback',
    tab: AI_REVIEW_TABS.LEARNINGS,
    keywords: [
      'learnings',
      'feedback',
      'memory',
      'training',
      'teach',
      'remember',
      'knowledge',
      'preferences',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.AUTO_FIX_LOOP,
    label: 'AI review - Auto-fix',
    tab: AI_REVIEW_TABS.AUTO_FIX,
    keywords: ['auto-fix', 'auto fix', 'autofix', 'fix automatically', 'fix loop'],
    // Alpha: only some installations see the card, so the palette does not offer it to all.
    commandEnabled: false,
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.FIX_WITH_CUBIC_TAGS,
    label: 'AI review - Fix from PR comments',
    tab: AI_REVIEW_TABS.AUTO_FIX,
    keywords: [
      'fix from pr comments',
      'tag cubic in chat to fix issues',
      'chat requested fixes',
      'fixes from pr comments',
      'pr comment fixes',
      'fix with cubic tags',
      'background agents',
      'claude code',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.FIX_WITH_CUBIC_BUTTON,
    label: 'AI review - Fix with cubic button',
    tab: AI_REVIEW_TABS.AUTO_FIX,
    keywords: [
      'fix with cubic button',
      'background agents',
      'auto fix',
      'fix button',
      'generate fix',
      'one-click fix',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.AUTO_FIX_PROVIDER,
    label: 'AI review - Coding agent provider',
    tab: AI_REVIEW_TABS.AUTO_FIX,
    keywords: [
      'fix provider',
      'auto fix provider',
      'cursor cloud agent',
      'cursor cloud agents',
      'background agents',
      'who applies fixes',
      'fix with cursor',
    ],
  },
  {
    id: AI_REVIEW_SETTINGS_CARD_IDS.CODING_AGENT_INSTRUCTIONS,
    label: 'AI review - Coding agent instructions',
    tab: AI_REVIEW_TABS.AUTO_FIX,
    keywords: [
      'coding agent instructions',
      'fix instructions',
      'auto-fix instructions',
      'agent instructions',
      'system prompt',
      'agent prompt',
    ],
  },
];

const AI_REVIEW_SETTINGS_CARD_ID_VALUES = Object.values(
  AI_REVIEW_SETTINGS_CARD_IDS,
) as AiReviewSettingsCardId[];

export const AI_REVIEW_SETTINGS_CARD_ID_SET = new Set<AiReviewSettingsCardId>(
  AI_REVIEW_SETTINGS_CARD_ID_VALUES,
);

export const AI_REVIEW_EXPERIMENTAL_CARD_IDS = new Set<AiReviewSettingsCardId>(
  AI_REVIEW_SETTINGS_CARDS.filter((card) => card.isExperimental).map((card) => card.id),
);

export const AI_REVIEW_SETTINGS_COMMAND_CARDS = AI_REVIEW_SETTINGS_CARDS.filter(
  (card) => card.commandEnabled !== false,
);

export function isAiReviewSettingsCardId(value: string | null): value is AiReviewSettingsCardId {
  return Boolean(value && AI_REVIEW_SETTINGS_CARD_ID_SET.has(value as AiReviewSettingsCardId));
}
